import React from 'react';
import { useStoreState } from 'easy-peasy';
import qs from 'querystringify';
import { omit } from 'lodash';
import moment from 'moment';

import Layout from '../../../components/global/Layout';
import Header from './Header';
import PlaceSearch from './Search';
import OrderDetailsTable from './OrderDetailsTable';
import { fetchUserOrdersDetails, transformUserOrderDetails } from './helpers';

const transformStartDate = startDate => {
  const originalTimestamp = moment.utc(startDate);
  const convertedTimestamp = originalTimestamp.set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  return convertedTimestamp.toISOString();
};

const transformEndDate = endDate => {
  const originalTimestamp = moment.utc(endDate);
  const convertedTimestamp = originalTimestamp.set({
    hour: 11,
    minute: 59,
    second: 0,
    millisecond: 0,
  });
  return convertedTimestamp.toISOString();
};

const currentDate = new Date();

const t = new Date();
t.setDate(t.getDate() - 30);

const OrderRefundsDashboard = ({ history, location }) => {
  const { userId } = useStoreState(state => state.auth);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [sort, setSort] = React.useState('DATE_DESC');

  const routeState = qs.parse(location.search);
  const {
    placename,
    username,
    useremail,
    userphone,
    orderRef,
    service,
    startDate,
    endDate,
    statusFilter,
    paymentStatus,
  } = routeState;

  const serviceTypes = {
    All: { label: 'ALL', value: 'All' },
    Pickup: { label: 'PICKUP', value: 'Pickup' },
    Delivery: { label: 'DELIVERY', value: 'Delivery' },
    Dinein: { label: 'DINEIN', value: 'Dinein' },
  };

  const statusTypes = {
    CANCELLED: { label: 'CANCELLED', value: 'CANCELLED' },
    COMPLETE: { label: 'COMPLETED', value: 'COMPLETE' },
    READY: { label: 'READY', value: 'READY' },
    CONFIRMED: { label: 'CONFIRMED', value: 'CONFIRMED' },
  };

  const paymentStatusType = {
    PAID_AND_PARTIAL_REFUND: { label: 'All', value: 'PAID_AND_PARTIAL_REFUND' },
    PAID: { label: 'Paid', value: 'PAID' },
    PARTIAL_REFUND: { label: 'Partial Refund', value: 'PARTIAL_REFUND' },
    FULL_REFUND: { label: 'Full Refund', value: 'FULL_REFUND' },
  };

  const search = {
    placename: placename ?? '',
    username: username ?? '',
    useremail: useremail ?? '',
    userphone: userphone ?? '',
    orderRef: orderRef ?? '',
    service: serviceTypes[service] ?? serviceTypes.All,
    startDate: startDate ?? transformStartDate(t),
    endDate: endDate ?? transformEndDate(currentDate),
    status: statusTypes[statusFilter] ?? {},
    paymentStatus: paymentStatusType[paymentStatus] ?? {
      label: 'Paid',
      value: 'PAID',
    },
  };

  console.log('search', search, routeState);

  const [totalPages, setTotalPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [count, setCount] = React.useState(0);

  const canLoadMoreData = currentPage + 1 < totalPages;

  const loadMoreDataFunc = () => {
    setCurrentPage(currentPage + 1);
  };

  React.useEffect(() => {
    let ignore = false;
    const fetchFunc = async () => {
      setLoading(true);
      const response = await fetchUserOrdersDetails({
        userId,
        from: currentPage * 50,
        placename: search.placename,
        orderRef: search.orderRef,
        service: search.service?.value,
        startDate: search.startDate,
        endDate: search.endDate,
        username: search.username,
        useremail: search.useremail,
        userphone: search.userphone,
        sort,
        status: search.status?.value,
        paymentStatus: search.paymentStatus?.value,
      });
      if (!ignore && response && currentPage === 0) {
        setOrders(transformUserOrderDetails(response.data));
        setTotalPages(response.totalPages);
        setCount(response.totalSize);
      } else if (!ignore && response) {
        setOrders([...orders, ...transformUserOrderDetails(response.data)]);
      }
      setLoading(false);
    };
    fetchFunc();
    return () => {
      ignore = true;
    };
  }, [
    search.placename,
    search.orderRef,
    search.service?.value,
    search.startDate,
    search.endDate,
    search.username,
    search.useremail,
    search.userphone,
    sort,
    currentPage,
    search.status?.value,
    search.paymentStatus?.value,
  ]);

  const handlePush = (args, key) => {
    setTotalPages(0);
    setCurrentPage(0);
    const newRoutState = omit(routeState, key);
    history.push(
      `/order-refunds-dashboard${qs.stringify(
        {
          ...newRoutState,
          ...args,
        },
        true,
      )}`,
    );
  };

  return (
    <Layout>
      <div style={{ position: 'relative' }}>
        <Header />
        <PlaceSearch
          clearPagination={() => {
            setCurrentPage(0);
            setTotalPages(0);
            setOrders([]);
          }}
          handlePush={handlePush}
          search={search}
        />
        {count === 0 ? (
          ''
        ) : (
          <div
            style={{ textAlign: 'right', padding: '1rem' }}
          >{`Total Orders : ${count}`}</div>
        )}
        {/* <div className="m-2">
          <legend className="label">
            Full refund orders are not displayed here.
          </legend>
        </div> */}
        <OrderDetailsTable
          loading={loading}
          userOrderDetailsData={orders}
          canLoadMoreData={canLoadMoreData}
          loadMoreData={loadMoreDataFunc}
          history={history}
          sort={sort}
          setSort={e => {
            setCurrentPage(0);
            setTotalPages(0);
            setOrders([]);
            setSort(e);
          }}
        />
      </div>
    </Layout>
  );
};

export default OrderRefundsDashboard;
